import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {Object} TextTruncateRefs
 * @property {HTMLElement} content - The truncatable content container
 * @property {HTMLButtonElement} toggle - The expand/collapse control
 */

/**
 * Truncates text to a set number of lines with a fade and animated expand/collapse.
 *
 * @extends {Component<TextTruncateRefs>}
 */
class TextTruncate extends Component {
  requiredRefs = ['content', 'toggle'];

  /** @type {boolean} */
  #expanded = false;

  /** @type {number} */
  #collapsedHeight = 0;

  /** @type {Animation | undefined} */
  #animation;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {number} */
  #animationDuration = 400;

  connectedCallback() {
    super.connectedCallback();

    this.#measure();

    this.#resizeObserver = new ResizeObserver(() => {
      if (this.#animation) return;
      this.#measure();
    });
    this.#resizeObserver.observe(this.refs.content);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#animation?.cancel();
  }

  /**
   * @param {HTMLElement} element
   * @returns {number}
   */
  #getLineHeight(element) {
    const style = getComputedStyle(element);
    const lineHeight = parseFloat(style.lineHeight);

    if (!Number.isNaN(lineHeight)) return lineHeight;

    const fontSize = parseFloat(style.fontSize);
    return Number.isNaN(fontSize) ? 24 : fontSize * 1.5;
  }

  #measure = () => {
    const { content, toggle } = this.refs;
    const lines = Number(this.dataset.lines || 4);
    const textElement = /** @type {HTMLElement} */ (content.firstElementChild || content);

    this.#collapsedHeight = Math.ceil(lines * this.#getLineHeight(textElement));

    const constrained = Boolean(content.style.height) && content.style.height !== 'auto';
    if (constrained) {
      content.style.height = 'auto';
      content.style.overflow = 'visible';
      content.style.maxHeight = 'none';
    }

    const fullHeight = content.scrollHeight;

    if (fullHeight <= this.#collapsedHeight + 2) {
      this.dataset.overflow = 'false';
      this.dataset.expanded = 'false';
      this.#expanded = false;
      content.style.height = '';
      content.style.overflow = '';
      content.style.maxHeight = '';
      toggle.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      this.dataset.ready = '';
      return;
    }

    this.dataset.overflow = 'true';
    toggle.hidden = false;

    if (this.#expanded) {
      content.style.height = 'auto';
      content.style.overflow = '';
      content.style.maxHeight = '';
      this.dataset.expanded = 'true';
      toggle.setAttribute('aria-expanded', 'true');
      this.dataset.ready = '';
      return;
    }

    content.style.maxHeight = 'none';
    content.style.height = `${this.#collapsedHeight}px`;
    content.style.overflow = 'hidden';
    this.dataset.expanded = 'false';
    toggle.setAttribute('aria-expanded', 'false');
    this.dataset.ready = '';
  };

  /**
   * @param {number} startHeight
   * @param {number} endHeight
   * @param {() => void} [onFinish]
   */
  #animateHeight(startHeight, endHeight, onFinish) {
    const { content } = this.refs;

    this.#animation?.cancel();

    if (prefersReducedMotion()) {
      content.style.height = this.#expanded ? 'auto' : `${endHeight}px`;
      content.style.overflow = this.#expanded ? '' : 'hidden';
      onFinish?.();
      return;
    }

    content.style.overflow = 'hidden';
    this.#animation = content.animate(
      {
        height: [`${startHeight}px`, `${endHeight}px`],
      },
      {
        duration: this.#animationDuration,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'forwards',
      }
    );

    this.#animation.onfinish = () => {
      content.style.height = this.#expanded ? 'auto' : `${endHeight}px`;
      content.style.overflow = this.#expanded ? '' : 'hidden';
      this.#animation = undefined;
      onFinish?.();
    };
  }

  /**
   * @param {Event} event
   */
  toggle = (event) => {
    event.preventDefault();

    if (this.dataset.overflow !== 'true') return;

    const { content, toggle } = this.refs;

    if (this.#expanded) {
      const startHeight = content.offsetHeight;
      this.#expanded = false;
      this.dataset.expanded = 'false';
      toggle.setAttribute('aria-expanded', 'false');
      this.#animateHeight(startHeight, this.#collapsedHeight);
      return;
    }

    const startHeight = content.offsetHeight;
    const endHeight = content.scrollHeight;
    this.#expanded = true;
    this.dataset.expanded = 'true';
    toggle.setAttribute('aria-expanded', 'true');
    this.#animateHeight(startHeight, endHeight, () => {
      content.style.height = 'auto';
    });
  };
}

if (!customElements.get('text-truncate')) {
  customElements.define('text-truncate', TextTruncate);
}
